import { useCallback, useMemo } from 'react';
import { DelayedDataSheet } from 'app/components/DelayedDataSheet';
import { replaceLineBreaks } from 'app/utils/helper-functions';
import { getTagHighlightColor, getTagIndicatorColor } from 'app/slate/extensions/tag';
import { resolveCellStyling } from 'app/utils/css';

function StyledDataSheet(props) {
  const {
    locale,
    taggedCells,
    highlightedCells,
    showOriginalValue,
    onTagClick,
    withTags,
    highlightedOption,
    tagDataMap,
    calcPickMode,
    termHighlightTagIds,
    cssClass,
    ...rest
  } = props;

  // Stable renderer identities; a new identity remounts every cell each render.
  const memoValueRenderer = useMemo(() => valueRenderer(locale, showOriginalValue), [locale, showOriginalValue]);
  const memoCellRenderer = useCallback(
    (cellProps) =>
      cellRenderer(cellProps, taggedCells, highlightedCells, onTagClick, withTags, highlightedOption, tagDataMap, calcPickMode, termHighlightTagIds),
    [taggedCells, highlightedCells, onTagClick, withTags, highlightedOption, tagDataMap, calcPickMode, termHighlightTagIds]
  );

  return (
    <DelayedDataSheet
      className={`table--reference-data${cssClass ? ' ' + cssClass : ''}`}
      overflow="wrap"
      valueRenderer={memoValueRenderer}
      cellRenderer={memoCellRenderer}
      valueViewer={valueViewer}
      onCellsChanged={onCellsChanged}
      onContextMenu={onContextMenu}
      {...rest}
    />
  );
}

export function onCellsChanged(changes) {
  const grid = this.xlsData;
  changes.forEach(({ cell, row, col, value }) => {
    grid[row][col] = { ...grid[row][col], value };
  });
  this.setState({ grid });
}

export function onContextMenu(e, cell, i, j) {
  return cell.readOnly ? e.preventDefault() : null;
}

export function valueRenderer(locale, showOriginalValue) {
  return function valueRenderer(cell) {
    // cell.readOnly = true;

    if (!!locale && !!cell.locales && Object.keys(cell.locales).length && !showOriginalValue) {
      return cell.locales[locale] || cell.value;
    }

    return cell.value;
  };
}

export function valueViewer(cell) {
  return replaceLineBreaks(cell.value);
}

function TagIndicator({ style }) {
  return (
    <div
      className="table-tag-indicator"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
      }}
    >
      <span style={{ position: 'absolute', top: '0', left: '0', height: '100%', borderRadius: '4px 0 0 4px', width: '4px', ...style }} />
      <span style={{ position: 'absolute', top: '0', right: '0', height: '100%', borderRadius: '0 4px 4px 0', width: '4px', ...style }} />
    </div>
  );
}

export function cellRenderer(props, taggedCells, highlightedCells, onTagClick, withTags, highlightedOption, tagDataMap, calcPickMode, termHighlightTagIds) {
  const { row, col, cell, children, attributesRenderer, updated, editing, className: _className, ...rest } = props;

  // Check if cell is tagged — collect ALL tags for this cell
  const cellTags = taggedCells?.filter((tag) => row === tag.cellRow && col === tag.cellCol) || [];
  const cellTag = cellTags[0];
  const isTagged = cellTags.length > 0;
  const cellTagIds = cellTags.map((t) => t.tagId);

  // Check if cell is highlighted (for backwards compatibility)
  const isHighlighted = !!highlightedCells?.find((highlight) => row === highlight.row && col === highlight.col);

  const { isHead, classes } = resolveCellStyling(cell);
  const Cell = isHead ? 'th' : 'td';

  const classNames = [_className, classes.join(' '), cell.background, cell.border];

  if (isHighlighted && !isTagged) {
    classNames.push('bg-warning');
  }

  const className = classNames.join(' ');
  const cellTagData = isTagged ? tagDataMap?.[cellTag.tagId] : null;

  let highlightColor = getTagHighlightColor(highlightedOption, cellTagData);
  let indicatorColor = getTagIndicatorColor(highlightedOption, cellTagData);
  if (isTagged && withTags) {
    const conceptId = cellTagData?.conceptId;
    const isPickParent = !!calcPickMode && !!conceptId && calcPickMode.parentConceptId === conceptId;
    const isPickedTerm = !!calcPickMode && !!conceptId && calcPickMode.termConceptIds?.has(conceptId);
    const isPickedCell = isPickedTerm && cellTagIds.some((id) => calcPickMode.pickedTagIds?.has(id));
    const isTermHighlighted = !calcPickMode && cellTagIds.some((id) => termHighlightTagIds?.has(id));
    if (isPickedCell || isTermHighlighted) {
      highlightColor = '#c4e8c9';
      indicatorColor = '#3d7a4f';
    } else if (isPickedTerm) {
      highlightColor = '#e8f3ea';
      indicatorColor = '#a8cbb0';
    } else if (calcPickMode && !isPickParent) {
      highlightColor = '#fdebc8';
      indicatorColor = '#d9a03f';
    }
  }

  // Style for tagged cells - use dynamic color based on highlighted option
  const taggedStyle =
    isTagged && withTags
      ? {
          backgroundColor: `${highlightColor}`,
          position: 'relative',
          borderRadius: '4px',
        }
      : {};

  const handleCellClick = isTagged && withTags ? () => onTagClick?.(cellTagIds) : undefined;
  const tagDataAttrs = isTagged && withTags ? { 'data-tag-ids': cellTagIds.join(' ') } : {};

  const renderContent = () => (
    <>
      {isTagged && withTags && <TagIndicator style={{ backgroundColor: indicatorColor }} />}
      {props.children}
    </>
  );

  if (cell.styling === 'bold') {
    return (
      <Cell className={`${className} xrp-bold xrp-table-bold`} {...rest} style={taggedStyle} onMouseUp={handleCellClick} {...tagDataAttrs}>
        {renderContent()}
      </Cell>
    );
  } else if (cell.styling === 'italic') {
    return (
      <Cell className={`${className} xrp-italic xrp-table-italic`} {...rest} style={taggedStyle} onMouseUp={handleCellClick} {...tagDataAttrs}>
        {renderContent()}
      </Cell>
    );
  } else if (cell.styling === 'underline') {
    return (
      <Cell className={`${className} xrp-underline`} {...rest} style={taggedStyle} onMouseUp={handleCellClick} {...tagDataAttrs}>
        {renderContent()}
      </Cell>
    );
  } else {
    return (
      <Cell className={className} {...rest} style={taggedStyle} onMouseUp={handleCellClick} {...tagDataAttrs}>
        {renderContent()}
      </Cell>
    );
  }
}

export default StyledDataSheet;
